/**
 * Judge Training Clip 1 — The Judge Interface
 * Orientation walkthrough pointing out key UI elements:
 * header/name, live indicator, heat tabs, notifications, riders, switch-to-live.
 * 480 frames at 30fps = 16 seconds.
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import { AutoCursor } from "../components/AutoCursor";
import { ScreenMock } from "../components/ScreenMock";
import { FadeTransition } from "../components/FadeTransition";
import { StepLabel } from "../components/StepLabel";

export function JudgeCourse1() {
  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0b", fontFamily: "'Inter', sans-serif" }}>
      <Sequence durationInFrames={55}><Title label="Judge Training · Clip 1" text="The Judge Interface" color="#3b82f6" /></Sequence>
      <Sequence from={45} durationInFrames={385}>
        <FadeTransition durationInFrames={385} fadeIn={10} fadeOut={10}><Demo /></FadeTransition>
      </Sequence>
      <Sequence from={420} durationInFrames={60}><Done text="You're ready to start scoring" /></Sequence>
    </AbsoluteFill>
  );
}

/* ── Demo ── */

function Demo() {
  const frame = useCurrentFrame();
  // At frame 310 switch to a non-live heat to show "Switch to live"
  const selectedHeatId = frame >= 310 ? "m-semis" : "m-round1";
  const showNotification = frame >= 185 && frame < 260;
  const showSwitchButton = selectedHeatId !== "m-round1";

  const activeSection =
    frame < 60 ? "header" :
    frame < 120 ? "live" :
    frame < 185 ? "heats" :
    frame < 255 ? "notification" :
    frame < 310 ? "riders" :
    "switch";

  return (
    <AbsoluteFill>
      <ScreenMock url="raley.koda-software.com/competition/boardstock/judge">
        <JudgeMock
          selectedHeatId={selectedHeatId}
          showNotification={showNotification}
          showSwitchButton={showSwitchButton}
          activeSection={activeSection}
        />
        <AutoCursor points={[
          { x: 960, y: 400, frame: 0 },
          { target: "judge-header", frame: 15 },
          { target: "judge-header", frame: 55 },
          { target: "judge-live", frame: 75 },
          { target: "judge-live", frame: 115 },
          { target: "judge-heats", frame: 130 },
          { target: "judge-heats", frame: 180 },
          { target: "judge-notification", frame: 195 },
          { target: "judge-notification", frame: 245 },
          { target: "judge-riders", frame: 255 },
          { target: "judge-riders", frame: 300 },
          { target: "ht-m-semis", frame: 305 },
          { target: "ht-m-semis", frame: 310, click: true },
          { target: "judge-switch-live", frame: 330 },
          { target: "judge-switch-live", frame: 375 },
        ]} />
      </ScreenMock>
      <StepLabel text="Check your name and competition details" show={5} hide={60} color="#3b82f6" />
      <StepLabel text="The red dot shows which heat is being broadcast" show={65} hide={120} color="#ef4444" />
      <StepLabel text="Heats are grouped by division — tap to switch" show={125} hide={185} color="#3b82f6" />
      <StepLabel text="Notifications alert you to score updates and heat changes" show={190} hide={255} color="#22c55e" />
      <StepLabel text="Each rider has attempt boxes — tap one to score it" show={260} hide={310} color="#3b82f6" />
      <StepLabel text="Not on the live heat? Tap 'Switch to live' to get back" show={315} hide={375} color="#eab308" />
    </AbsoluteFill>
  );
}

/* ── Types ── */

interface Group {
  id: string;
  name: string;
  heats: { id: string; name: string }[];
}

interface Rider {
  name: string;
  nick: string;
  attempts: (number | null)[];
  maxAttempts: number;
  currentAttempt: number;
}

/* ── Data ── */

const groups: Group[] = [
  {
    id: "mens",
    name: "MEN'S OPEN",
    heats: [
      { id: "m-round1", name: "Round 1" },
      { id: "m-semis", name: "Semi-Finals" },
      { id: "m-finals", name: "Finals" },
    ],
  },
  {
    id: "womens",
    name: "WOMEN'S OPEN",
    heats: [
      { id: "w-round1", name: "Round 1" },
      { id: "w-finals", name: "Finals" },
    ],
  },
];

const riderSets: Record<string, Rider[]> = {
  "m-round1": [
    { name: "Harley Clifford", nick: "The King", attempts: [8.7, 9.1, 7.5], maxAttempts: 3, currentAttempt: 3 },
    { name: "Nic Rapa", nick: "Raptor", attempts: [7.2, 6.8, null], maxAttempts: 3, currentAttempt: 3 },
    { name: "Steel Lafferty", nick: "Steele", attempts: [null, null, null], maxAttempts: 3, currentAttempt: 1 },
    { name: "Cory Teunissen", nick: "CT", attempts: [6.4, 7.1, null], maxAttempts: 3, currentAttempt: 2 },
    { name: "Bob Soven", nick: "Bobby", attempts: [null, null, null], maxAttempts: 3, currentAttempt: 1 },
    { name: "Tony Iacconi", nick: "Tone", attempts: [8.1, 8.5, 7.9], maxAttempts: 3, currentAttempt: 3 },
  ],
  "m-semis": [
    { name: "Harley Clifford", nick: "The King", attempts: [9.3, null, null], maxAttempts: 3, currentAttempt: 2 },
    { name: "Cory Teunissen", nick: "CT", attempts: [null, null, null], maxAttempts: 3, currentAttempt: 1 },
    { name: "Tony Iacconi", nick: "Tone", attempts: [7.8, 8.2, null], maxAttempts: 3, currentAttempt: 2 },
    { name: "Nic Rapa", nick: "Raptor", attempts: [8.5, 7.9, null], maxAttempts: 3, currentAttempt: 3 },
  ],
};

/* ── Mock ── */

function JudgeMock({
  selectedHeatId,
  showNotification,
  showSwitchButton,
  activeSection,
}: {
  selectedHeatId: string;
  showNotification: boolean;
  showSwitchButton: boolean;
  activeSection: string;
}) {
  const riders = riderSets[selectedHeatId] ?? riderSets["m-round1"];

  const glow = (section: string): React.CSSProperties =>
    activeSection === section
      ? { boxShadow: "0 0 0 2px #3b82f6, 0 0 15px rgba(59,130,246,0.25)", borderColor: "#3b82f6" }
      : {};

  return (
    <div style={shell}>
      {/* Top bar */}
      <div style={topbar}>
        <span style={brand}>RALEY</span>
        <div style={{ display: "flex", gap: 16, marginLeft: 32 }}>
          {["Competitions", "Riders", "Training"].map((label) => (
            <span key={label} style={{ fontSize: 11, color: "#52525b", fontWeight: 500 }}>{label}</span>
          ))}
        </div>
        <span style={{ marginLeft: "auto", ...mono, fontSize: 11, color: "#71717a" }}>sarah@email.com</span>
      </div>

      <div style={{ padding: "16px 20px", position: "relative" }}>
        {/* Page header */}
        <div
          id="judge-header"
          style={{
            padding: "8px 12px 12px",
            marginBottom: 12,
            border: "1px solid #2a2a32",
            ...glow("header"),
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>
            Judge: Sarah
          </div>
          <div style={{ fontSize: 11, color: "#71717a", marginTop: 2 }}>
            Boardstock Invitational · average scoring
          </div>
        </div>

        {/* Live heat indicator */}
        <div
          id="judge-live"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 14px",
            marginBottom: 16,
            background: "#111113",
            border: "1px solid #2a2a32",
            ...glow("live"),
          }}
        >
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444", boxShadow: "0 0 6px #ef4444", flexShrink: 0 }} />
          <span style={{ fontSize: 12, fontWeight: 600 }}>
            Live Heat: <span style={{ color: "#3b82f6" }}>Men&apos;s Open — Round 1</span>
          </span>
          {showSwitchButton && (
            <button
              id="judge-switch-live"
              style={{
                marginLeft: "auto",
                padding: "4px 10px",
                fontSize: 10,
                fontWeight: 600,
                background: "#1a1a1e",
                color: "#e4e4e7",
                border: "1px solid #2a2a32",
                cursor: "pointer",
                ...(activeSection === "switch"
                  ? { boxShadow: "0 0 0 2px #eab308, 0 0 15px rgba(234,179,8,0.25)", borderColor: "#eab308" }
                  : {}),
              }}
            >
              Switch to live
            </button>
          )}
        </div>

        {/* Heat tabs */}
        <div
          id="judge-heats"
          style={{
            marginBottom: 16,
            padding: "8px 12px",
            border: "1px solid transparent",
            ...glow("heats"),
          }}
        >
          {groups.map((g) => (
            <div key={g.id} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, fontWeight: 600, color: "#52525b", ...mono }}>
                {g.name}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {g.heats.map((h) => (
                  <div
                    key={h.id}
                    id={`ht-${h.id}`}
                    style={{
                      padding: "3px 10px",
                      fontSize: 10,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      background: selectedHeatId === h.id ? "#3b82f6" : "#1a1a1e",
                      color: selectedHeatId === h.id ? "#fff" : "#71717a",
                      border: `1px solid ${selectedHeatId === h.id ? "#3b82f6" : "#2a2a32"}`,
                      cursor: "pointer",
                    }}
                  >
                    {h.name}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Column header */}
        <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 8, padding: "0 8px", marginBottom: 4, alignItems: "center" }}>
          <span style={colLabel}>Rider</span>
          <span style={colLabel}>Attempts / Score</span>
        </div>

        {/* Rider rows */}
        <div
          id="judge-riders"
          style={{ border: "1px solid transparent", ...glow("riders") }}
        >
          {riders.map((r, ri) => {
            const hasAnyScore = r.attempts.some((a) => a !== null);
            return (
              <div
                key={`${r.name}-${ri}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "160px 1fr",
                  gap: 8,
                  padding: "6px 8px",
                  alignItems: "center",
                  background: hasAnyScore ? "#111113" : "transparent",
                  border: "1px solid #2a2a32",
                  marginBottom: 2,
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 11 }}>{r.name}</div>
                  <div style={{ fontSize: 9, color: "#52525b" }}>{r.nick}</div>
                </div>
                <div style={{ display: "flex", gap: 2 }}>
                  {Array.from({ length: r.maxAttempts }, (_, i) => {
                    const score = r.attempts[i] ?? null;
                    const hasScore = score !== null;
                    const isAvailable = i + 1 <= r.currentAttempt;
                    const isFuture = !isAvailable;
                    const needsScore = isAvailable && !hasScore;
                    const isLive = i + 1 === r.currentAttempt;
                    return (
                      <div
                        key={i}
                        style={{
                          flex: "1 1 0",
                          minWidth: 0,
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                          fontSize: hasScore ? 11 : 9,
                          fontWeight: hasScore ? 700 : 500,
                          ...mono,
                          background: hasScore ? "#1a1a1e" : needsScore ? "rgba(234,179,8,0.1)" : "transparent",
                          color: hasScore ? "#e4e4e7" : needsScore ? "#eab308" : isFuture ? "#2a2a32" : "#52525b",
                          border: `1px solid ${needsScore ? "#eab308" : isLive ? "#22c55e" : "#2a2a32"}`,
                          opacity: isFuture ? 0.3 : 1,
                        }}
                      >
                        {hasScore ? score.toFixed(1) : `#${i + 1}`}
                        {needsScore && <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#eab308" }} />}
                        {isLive && hasScore && <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#22c55e" }} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Notification toast */}
        {showNotification && (
          <div
            id="judge-notification"
            style={{
              position: "absolute",
              top: 16,
              right: 20,
              background: "#1a1a1e",
              border: `1px solid ${activeSection === "notification" ? "#22c55e" : "#2a2a32"}`,
              padding: "10px 16px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              boxShadow: activeSection === "notification"
                ? "0 0 0 2px #22c55e, 0 4px 20px rgba(0,0,0,0.5)"
                : "0 4px 20px rgba(0,0,0,0.5)",
              zIndex: 10,
            }}
          >
            <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#8b5cf6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#fff", ...mono }}>J2</div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600 }}>scored Harley Clifford #1</div>
              <div style={{ fontSize: 10, color: "#71717a", ...mono }}>8.5</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Shared ── */

function Title({ label, text, color }: { label: string; text: string; color: string }) {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12, 40, 55], [0, 1, 1, 0], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ fontSize: 10, color, textTransform: "uppercase", letterSpacing: 3, fontFamily: "'JetBrains Mono', monospace", marginBottom: 14 }}>{label}</div>
      <div style={{ fontSize: 52, fontWeight: 800, color: "#fff" }}>{text}</div>
    </AbsoluteFill>
  );
}

function Done({ text }: { text: string }) {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}><span style={{ fontSize: 24, color: "#fff" }}>&#10003;</span></div>
      <div style={{ fontSize: 28, fontWeight: 700, color: "#fff" }}>{text}</div>
    </AbsoluteFill>
  );
}

const shell: React.CSSProperties = { width: "100%", height: "100%", background: "#0a0a0b", fontFamily: "'Inter', sans-serif", color: "#e4e4e7", display: "flex", flexDirection: "column" };
const topbar: React.CSSProperties = { height: 40, background: "#111113", borderBottom: "1px solid #2a2a32", display: "flex", alignItems: "center", gap: 24, padding: "0 20px", flexShrink: 0 };
const brand: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, fontSize: 14, letterSpacing: 2 };
const mono: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace" };
const colLabel: React.CSSProperties = { fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "#52525b", fontFamily: "'JetBrains Mono', monospace" };
